<script setup lang="ts">
import { computed, type PropType, provide } from "vue";
import type { TabBoxConfig, UIColor } from "../../..";
import { TabBoxConfigInjectionKey } from "../../helpers.js";

const props = defineProps({
  colorBackground: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorBackgroundActive: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorBackgroundHover: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorLabelActive: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorLabelInactive: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorScrollBackground: {
    type: String as PropType<UIColor>,
    required: true,
  },

  colorScrollForeground: {
    type: String as PropType<UIColor>,
    required: true,
  },

  wrap: {
    type: Boolean,
    default: false,
  },

  v2activeMode: {
    type: Boolean,
    default: false,
  },
});

const tabBoxConfig = computed<TabBoxConfig>(() => ({
  colorBackgroundActive: props.colorBackgroundActive,
  colorBackgroundHover: props.colorBackgroundHover,
  colorLabelActive: props.colorLabelActive,
  colorLabelInactive: props.colorLabelInactive,
  v2activeMode: props.v2activeMode,
}));

provide(TabBoxConfigInjectionKey, tabBoxConfig);
</script>

<template>
  <div
    :class="[
      'tab-box',
      $style.container,
      wrap && $style.wrap,
      wrap && 'tab-box-wrap',
    ]"
    :style="{
      'background-color': `var(${colorBackground})`,
      '--color-scroll-background': `var(${
        colorScrollBackground || colorBackgroundHover
      })`,
      '--color-scroll-foreground': `var(${
        colorScrollForeground || colorBackgroundActive
      })`,
    }"
  >
    <slot name="items" />
  </div>
</template>

<style module>
.container {
  display: flex;
  width: 100%;
  padding: 0.25rem 0 0 0.25rem;
  min-height: 2.5rem;
  clip-path: inset(0 0 0 0 round 0);
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: var(--color-scroll-foreground) var(--color-scroll-background);
  scrollbar-width: thin;
  user-select: none;
}

.container::-webkit-scrollbar {
  height: 1rem;
}

.container::-webkit-scrollbar-track {
  background: var(--color-scroll-background);
}

.container::-webkit-scrollbar-thumb {
  background-color: var(--color-scroll-foreground);
  border-radius: calc(1rem - var(--border-radius-medium));
  border: 0.25rem solid var(--color-scroll-background);
}

.container::-webkit-scrollbar-thumb:hover {
  border-width: 0.2rem;
}

.wrap {
  flex-wrap: wrap;
  max-height: 18.85vh;
}

:global(.tab-box.tab-box-wrap .tab-box-item) {
  width: calc(100% - 0.25rem - 0.25rem);
  overflow-y: hidden;
}
</style>
